import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useGetMe from '../useCurrentUser';
import { IAppState } from '../../store/types';
import { setJustLoggedIn } from '../../store/user/actions';
import { BaseService } from '../../global/classes';

import { NpsEligibility } from '../../components/NpsSurveyModal/types';
import { NpsAnswerService } from '../../components/NpsSurveyModal/services/NpsAnswerService';
import { SplashDTO } from '../../pages/splash/types';
import { fetchDisplayableSplash } from '../../pages/splash/actions';

export type PostLoginModal =
  | { type: 'nps'; eligibility: NpsEligibility }
  | { type: 'splash'; splash: SplashDTO };

const resolvePostLoginModal = async (
  userId: string,
  profileId: number
): Promise<PostLoginModal | null> => {
  const businessId = new BaseService().selectedCompany?.id;

  const eligibility = businessId
    ? await new NpsAnswerService()
        .getEligibility(businessId, userId)
        .catch((error) => console.log(error))
    : null;

  if (eligibility) return { type: 'nps', eligibility };

  const splash = await fetchDisplayableSplash(userId, profileId);

  return splash ? { type: 'splash', splash } : null;
};

const usePostLoginModal = () => {
  const dispatch = useDispatch();
  const { id: userId, userType: profileId } = useGetMe();

  const justLoggedIn = useSelector((state: IAppState) => state.user.justLoggedIn);

  const [modal, setModal] = useState<PostLoginModal | null>(null);

  const hasConsumedLoginRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (!justLoggedIn || hasConsumedLoginRef.current) return;
    if (!userId || !profileId) return;

    hasConsumedLoginRef.current = true;
    dispatch(setJustLoggedIn(false));

    resolvePostLoginModal(userId, profileId).then((resolved) => {
      if (isMountedRef.current) setModal(resolved);
    });
  }, [justLoggedIn, userId, profileId, dispatch]);

  const closeModal = useCallback(() => setModal(null), []);

  return { modal, closeModal };
};

export default usePostLoginModal;
